'use client';

import { MessageIcon } from '@insta-club/components/icons';
import { Calendar } from '@insta-club/models';
import { useViewModeStore } from '@insta-club/state';
import { cn, isBeforeNowJalaali, isSameJalaaliDate, toOrdinalPersian } from '@insta-club/utilities';
import { useEffect, useRef } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

interface Props {
    value?: Calendar;
    calendar: Calendar[];
    onChange?: (day: Calendar) => void;
}

const ProgramCalendar: React.FC<Props> = ({ value, calendar, onChange }) => {
    const swiperRef = useRef<SwiperType | null>(null);
    // const setCalendarView = useViewModeStore((state) => state.setCalendarView);
    const calendarView = useViewModeStore((state) => state.calendarView);

    useEffect(() => {
        if (!swiperRef.current || !value || calendarView !== 'slide') return;
        const swiper = swiperRef.current;
        const index = calendar.findIndex((d) => d.day === value.day);
        if (index < 0) return;
        const slideEl = swiper.slides[index];
        const swiperEl = swiper.el;
        const slideRect = slideEl.getBoundingClientRect();
        const swiperRect = swiperEl.getBoundingClientRect();
        const isVisible = slideRect.left >= swiperRect.left && slideRect.right <= swiperRect.right;
        if (!isVisible) {
            swiper.slideTo(index);
        }
    }, [value, calendar, calendarView]);

    const getDayOfWeekIndex = (dayName: string): number => {
        const days: { [key: string]: number } = {
            شنبه: 0,
            یکشنبه: 1,
            دوشنبه: 2,
            'سه شنبه': 3,
            چهارشنبه: 4,
            پنجشنبه: 5,
            'پنج شنبه': 5,
            جمعه: 6
        };
        return days[dayName] ?? 0;
    };

    const renderDayItemGrid = (day: Calendar, index: number) => {
        const isSelected = value?.day === day.day;
        const hasPost = calendar.find((c) => c.day === day.day)?.post;

        return (
            <div key={`day-${day.day}-${index}`} className='space-y-0.5 text-center'>
                <div
                    onClick={() => onChange?.(day)}
                    className={cn(
                        'border-primary-100 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-full border bg-white pt-3 pb-3',
                        {
                            'bg-primary-200 border-transparent text-gray-700': isSameJalaaliDate(day.date),
                            'bg-primary-100 border-t-primary-100 border-t-4': isBeforeNowJalaali(day.date),
                            'bg-primary-500 border-t-primary-500 border-t-4 text-white': isSelected,
                            'border-t-primary-800 border-t-4': hasPost
                        }
                    )}
                >
                    {isSameJalaaliDate(day.date) ? (
                        <span
                            className={cn('flex min-h-[30px] items-center justify-center text-xs font-bold', {
                                'text-white': isSelected
                            })}
                        >
                            امروز
                        </span>
                    ) : (
                        <div
                            className={cn('block min-h-[30px] text-[10px] font-semibold lg:text-xs', {
                                'text-white': isSelected,
                                'text-gray-800': !isSelected
                            })}
                        >
                            <span>
                                روز
                                <br />
                                {toOrdinalPersian(day.day)}
                            </span>
                        </div>
                    )}

                    <span
                        className={cn('mt-0.5 block text-xs font-medium text-gray-600 lg:text-sm', {
                            'text-gray-200': isSelected,
                            'text-gray-800': !isSelected
                        })}
                    >
                        {day.date.month}/{day.date.day}
                    </span>

                    {day.notes_count > 0 && (
                        <div className='mx-auto flex h-[20px] w-[20px] items-center justify-center rounded-full text-sm font-medium'>
                            <MessageIcon
                                width={16}
                                className={cn('text-primary-800', {
                                    'text-white': isSelected
                                })}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const createCalendarGrid = () => {
        if (calendar.length === 0) {
            return { weekDays: [], grid: [] };
        }

        const firstDay = calendar[0];

        const startDayIndex = getDayOfWeekIndex(firstDay.date.dayOfWeek);

        console.log(startDayIndex, firstDay.date.dayOfWeek);

        const allWeekDays = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];
        const weekDays: string[] = [];

        for (let i = 0; i < 7; i++) {
            weekDays.push(allWeekDays[(startDayIndex + i) % 7]);
        }

        const grid: Array<Array<Calendar | null>> = [[], []];

        for (let i = 0; i < 2; i++) {
            for (let j = 0; j < 7; j++) {
                grid[i][j] = null;
            }
        }

        const daysToShow = calendar.slice(0, 14);

        daysToShow.forEach((day, index) => {
            const weekIndex = Math.floor(index / 7);
            const dayIndex = index % 7;

            if (weekIndex < 2) {
                grid[weekIndex][dayIndex] = day;
            }
        });

        return { weekDays, grid };
    };

    const { weekDays, grid } = createCalendarGrid();

    return (
        <div className='space-y-4'>
            {/* SWITCH BUTTON */}
            {/* <div className='flex justify-end'>
                <div className='flex overflow-hidden rounded-full bg-gray-200 text-[10px] font-semibold transition-colors'>
                    {calendarModes.map((mode, index) => (
                        <button
                            key={mode.key}
                            type='button'
                            onClick={() => setCalendarView(mode.key)}
                            className={cn(
                                'px-2 py-0.5 transition-colors duration-200 focus:outline-none',
                                calendarView === mode.key
                                    ? 'bg-gray-700 text-white'
                                    : 'text-gray-700 hover:bg-gray-400/50',
                                index === 0 ? 'rounded-r-full pr-2.5' : 'rounded-l-full pl-2.5'
                            )}
                        >
                            {mode.key === 'grid' ? <GridIcon height={16} /> : <SliderIcon height={16} />}
                        </button>
                    ))}
                </div>
            </div> */}
            {/* <div className='flex justify-end'>
                <button
                    onClick={() => setViewMode(viewMode === 'slider' ? 'grid' : 'slider')}
                    className='text-sm font-medium'
                >
                    {viewMode === 'slider' ? 'نمایش تقویمی' : 'نمایش اسلایدری'}
                </button>
            </div> */}

            {/* SLIDER */}
            {calendarView === 'slide' && (
                <Swiper
                    spaceBetween={8}
                    slidesPerView='auto'
                    slidesOffsetAfter={12}
                    slidesOffsetBefore={12}
                    style={{ overflow: 'visible', overflowX: 'clip' }}
                    onSwiper={(swiper) => (swiperRef.current = swiper)}
                    className='rounded-r-2xl bg-gray-50'
                >
                    {calendar.map((day) => {
                        const isSelected = value?.day === day.day;
                        return (
                            <SwiperSlide
                                onClick={() => onChange?.(day)}
                                key={`day-${day.day}`}
                                style={{ width: 50 }}
                                className='my-3'
                            >
                                <div className='space-y-0.5 text-center'>
                                    <div
                                        className={cn('mb-0.5 block text-[10px] font-normal', {
                                            'text-primary-500': isSelected
                                        })}
                                    >
                                        <span>روز {toOrdinalPersian(day.day)}</span>
                                    </div>
                                    <div
                                        className={cn(
                                            'shadow-main flex cursor-pointer flex-col items-center justify-center gap-1 rounded-full border-[1.5px] border-transparent bg-white pt-6 pb-3 text-gray-700',
                                            {
                                                'bg-primary-500 text-white': isSameJalaaliDate(day.date),
                                                'bg-primary-100': isBeforeNowJalaali(day.date),
                                                'border-primary-500': isSelected
                                            }
                                        )}
                                    >
                                        <span className={'block text-xs font-bold'}>
                                            {isSameJalaaliDate(day.date) ? 'امروز' : day.date.dayOfWeek}
                                        </span>
                                        <span className={'mt-1 mb-2 block text-xs font-medium'}>
                                            {day.date.month}/{day.date.day}
                                        </span>
                                    </div>
                                    {day.notes_count > 0 && (
                                        <div className='mx-auto flex h-[20px] w-[20px] items-center justify-center space-x-0.5 rounded-full text-sm font-medium'>
                                            <MessageIcon width={16} className='text-primary-500' />
                                        </div>
                                    )}
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            )}

            {/* GRID */}
            {calendarView === 'grid' && (
                <div className=''>
                    <div className='mb-3 grid grid-cols-7 gap-1'>
                        {weekDays.map((day, index) => (
                            <div key={`${day}-${index}`} className='text-center text-xs font-semibold text-gray-600'>
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className='space-y-2'>
                        {grid.map((week, weekIndex) => (
                            <div key={weekIndex} className='grid grid-cols-7 gap-2'>
                                {week.map((day, dayIndex) => (
                                    <div key={`${weekIndex}-${dayIndex}`}>
                                        {day ? (
                                            renderDayItemGrid(day, weekIndex * 7 + dayIndex)
                                        ) : (
                                            <div className='h-full'></div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProgramCalendar;

